import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAuthCookie } from "utils/authCookies";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import "components/SearchBrokerageAgentTeam.css";

type MigrationMode = "agentToTeam" | "teamToAgent";
type LicenseType = "BROKERAGE_FIRM" | "BROKERAGE_AGENT" | "BROKERAGE_TEAM_MEMBER" | "OFFICE";

interface ApiOption {
  value?: string;
  id?: string;
  _id?: string;
  label?: string;
  text?: string;
  name?: string;
  title?: string;
  email?: string;
  license_type?: string;
  phone1?: string;
  office_count?: number;
  offices_count?: number;
  company_name?: string;
  brokerage_name?: string;
  officeName?: string;
  office_name?: string;
  logo_url?: string;
  profile_pic_url?: string;
  image?: string;
  office?: OfficeLike;
  offices?: OfficeLike | OfficeLike[];
  firstName?: string;
  lastName?: string;
}

interface OfficeLike {
  id?: string;
  _id?: string;
  value?: string;
  label?: string;
  name?: string;
  officeName?: string;
  office_name?: string;
}

interface JobTitleOption {
  id: string;
  label: string;
  value: string;
}

const API_BASE = process.env.REACT_APP_API_URL || "";
const DEFAULT_LIMIT = 10;
const DEBOUNCE_MS = 300;
const DEFAULT_IMAGE = imagecustomRounded;

const modeConfig = {
  agentToTeam: {
    previewPath: "brokerage-agent-to-team-member",
    sourceLicenseType: "BROKERAGE_AGENT" as LicenseType,
    targetLicenseType: "BROKERAGE_TEAM_MEMBER" as LicenseType,
    sourceLabel: "Agent",
    targetLabel: "Brokerage Team Member",
    searchLabel: "Search Agent",
    searchPlaceholder: "Search by agent name, email or office name.",
    needJobTitle: true,
    banner: (
      <>
        Use this to change an <b>Agent</b> into a <b>Brokerage Teammember</b> within the same organization. Choose the brokerage
        organization first, then select the agent and set their job title and office.
      </>
    ),
  },
  teamToAgent: {
    previewPath: "brokerage-team-member-to-agent",
    sourceLicenseType: "BROKERAGE_TEAM_MEMBER" as LicenseType,
    targetLicenseType: "BROKERAGE_AGENT" as LicenseType,
    sourceLabel: "Teammember",
    targetLabel: "Brokerage Agent",
    searchLabel: "Search Teammember",
    searchPlaceholder: "Search by teammember name, email or office name.",
    needJobTitle: false,
    banner: (
      <>
        Use this to change a <b>Brokerage Teammember</b> into an <b>Agent</b> within the same organization. Choose the brokerage
        organization first, then select the teammember.
      </>
    ),
  },
};
const JOB_TITLE_CUSTOM = "Custom";

function getOptionId(item?: ApiOption | null) {
  return String(item?.value || item?.id || item?._id || "").trim();
}

function getDisplayName(item?: ApiOption | null) {
  if (!item) return "";
  if (item.license_type === "BROKERAGE_FIRM" && item.company_name) return item.company_name;
  if (String(item.license_type || "").toUpperCase() === "OFFICE") {
    return getOfficeName(item) || item.label || item.text || item.name || "";
  }
  const fullName = `${item.firstName || ""} ${item.lastName || ""}`.trim();
  return item.label || item.text || fullName || item.name || item.company_name || item.brokerage_name || "";
}

function getOfficeName(item?: ApiOption | null) {
  if (!item) return "";
  const offices = Array.isArray(item.offices) ? item.offices : item.offices ? [item.offices] : [];
  const firstOffice = item.office || offices[0];
  const isOfficeRow = String(item.license_type || "").toUpperCase() === "OFFICE";
  return (
    item.officeName ||
    item.office_name ||
    firstOffice?.officeName ||
    firstOffice?.office_name ||
    firstOffice?.label ||
    firstOffice?.name ||
    (isOfficeRow ? item.label || item.text || item.name || item.title : "") ||
    ""
  );
}

function normalizeApiOption(item: ApiOption, fallbackLicenseType: LicenseType): ApiOption {
  const licenseType = String(item.license_type || fallbackLicenseType).toUpperCase();
  const normalized: ApiOption = {
    ...item,
    license_type: licenseType,
  };

  if (licenseType === "OFFICE") {
    const officeName = getOfficeName(normalized) || item.label || item.text || item.name || item.title || "";
    return {
      ...normalized,
      label: officeName,
      officeName,
      value: getOptionId(item) || String(item.office?.id || item.office?._id || item.office?.value || ""),
    };
  }

  return normalized;
}

function formatEmail(email?: string) {
  const clean = String(email || "").trim().split("||")[0].trim();
  return clean || "--";
}

function formatOfficeCount(item?: ApiOption | null) {
  const count = item?.office_count ?? item?.offices_count;
  if (count === undefined || count === null || String(count).trim() === "") return "--";
  const normalized = Number(count);
  if (!Number.isFinite(normalized)) return String(count);
  return `${normalized} office${normalized === 1 ? "" : "s"}`;
}

function licenseLabel(licenseType?: string) {
  if (licenseType === "BROKERAGE_TEAM_MEMBER") return "Brokerage Team Member";
  if (licenseType === "BROKERAGE_AGENT") return "Brokerage Agent";
  if (licenseType === "BROKERAGE_FIRM") return "Brokerage";
  if (licenseType === "OFFICE") return "Office";
  return "--";
}

function badgeForLicense(licenseType?: string) {
  if (licenseType === "BROKERAGE_FIRM") return { label: "Brokerage", className: "search-bat-badge-b" };
  if (licenseType === "OFFICE") return { label: "Office", className: "search-bat-badge-o" };
  if (licenseType === "BROKERAGE_TEAM_MEMBER") return { label: "Team Member", className: "search-bat-badge-m" };
  return { label: "Agent", className: "search-bat-badge-ba" };
}

function optionMeta(item: ApiOption) {
  if (item.license_type === "BROKERAGE_FIRM") return formatOfficeCount(item);
  if (item.license_type === "OFFICE") return item.company_name || item.brokerage_name || "--";
  const company = item.company_name || item.brokerage_name || "--";
  const office = getOfficeName(item) || "--";
  return `${company}, ${office}`;
}

async function fetchCreatedOnlyUsers({
  ownerId,
  token,
  query,
  page,
  licenseType,
  brokerageFirmId,
  signal,
}: {
  ownerId: string;
  token: string;
  query: string;
  page: number;
  licenseType: LicenseType;
  brokerageFirmId?: string;
  signal?: AbortSignal;
}) {
  const params = new URLSearchParams({
    licenseType,
    query: query.trim(),
    page: String(page),
    limit: String(DEFAULT_LIMIT),
  });

  if (brokerageFirmId) {
    params.set("brokerageFirmID", brokerageFirmId);
  }

  const response = await fetch(
    `${API_BASE}/customer-groups/${ownerId}/users-created-only?${params}`,
    {
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  return response.json();
}

type MigrationRequestBody = {
  target_brokerage_id: string;
  office_ids: string[];
  job_title?: string;
  job_title_id?: string;
};

type ActiveProjectPreview = {
  id?: string;
  project_number?: string;
  title?: string;
  status?: string;
};

type WarningNamedItem = {
  id?: string;
  name?: string;
  groups?: Array<{ id?: string; name?: string }>;
};

type MigrationPreview = {
  allowed?: boolean;
  blockers?: Record<string, number | string | null | undefined>;
  blocking_reasons?: string[];
  info?: {
    active_projects?: ActiveProjectPreview[];
  };
  warnings?: {
    customer_groups?: WarningNamedItem[];
    payment_methods?: WarningNamedItem[];
    discounts?: WarningNamedItem[];
    special_fees?: WarningNamedItem[];
  };
};

type PreviewPanelKey = "blockers" | "reasons" | "projects" | "warnings";

async function postMigrationAction(
  migratedUserId: string,
  token: string,
  previewPath: string,
  action: "preview" | "execute",
  body: MigrationRequestBody
) {
  const response = await fetch(`${API_BASE}/users/${migratedUserId}/migrations/${previewPath}/${action}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const fallback = action === "preview" ? "Migration preview failed" : "Migration failed";
    throw new Error(data?.message || data?.error || response.statusText || fallback);
  }

  return data;
}

async function createCustomJobTitle(token: string, title: string) {
  const response = await fetch(`${API_BASE}/job-titles`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      title,
      description: "",
      status: true,
    }),
  });

  const result = await response.json();
  if (!response.ok || result?.error) {
    throw new Error(result?.message || result?.error || "Failed to create job title");
  }

  return {
    title: String(result?.title || title),
    id: String(result?.id || result?._id || "").trim(),
  };
}

function formatBlockerLabel(key: string) {
  return key
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function hasPreviewBlockers(preview: MigrationPreview | null) {
  return Boolean(preview?.blockers && Object.keys(preview.blockers).length > 0);
}

function hasBlockingReasons(preview: MigrationPreview | null) {
  return Boolean(preview?.blocking_reasons?.length);
}

function hasActiveProjects(preview: MigrationPreview | null) {
  return Boolean(preview?.info?.active_projects?.length);
}

function hasWarnings(preview: MigrationPreview | null) {
  const warnings = preview?.warnings;
  if (!warnings) return false;
  return Boolean(
    (warnings.customer_groups?.length ?? 0) > 0 ||
      (warnings.payment_methods?.length ?? 0) > 0 ||
      (warnings.discounts?.length ?? 0) > 0 ||
      (warnings.special_fees?.length ?? 0) > 0
  );
}

function hasAnyPreviewAccordionContent(preview: MigrationPreview | null) {
  return (
    hasPreviewBlockers(preview) ||
    hasBlockingReasons(preview) ||
    hasActiveProjects(preview) ||
    hasWarnings(preview)
  );
}

function buildExpandedPreviewPanels(preview: MigrationPreview | null): Record<PreviewPanelKey, boolean> {
  return {
    blockers: hasPreviewBlockers(preview),
    reasons: false,
    projects: false,
    warnings: false,
  };
}

const MIGRATION_WARNINGS_REMOVAL_NOTE =
  "The customer groups, payment methods, discounts, and special fees listed above will be removed from this user once you click Yes, Migrate.";

function formatProjectStatus(status?: string) {
  const raw = String(status || "").trim();
  if (!raw) return "--";
  return raw
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function togglePreviewPanel(
  key: PreviewPanelKey,
  setExpandedPreviewPanels: React.Dispatch<React.SetStateAction<Record<PreviewPanelKey, boolean>>>
) {
  setExpandedPreviewPanels((prev) => ({ ...prev, [key]: !prev[key] }));
}

export default function SignageMigrations() {
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const signageId = isSignageTeammember ? parentSignageId || "" : userId;

  const [mode, setMode] = useState<MigrationMode>("agentToTeam");
  const [selectedBrokerage, setSelectedBrokerage] = useState<ApiOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<ApiOption | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<ApiOption | null>(null);
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [customJobTitle, setCustomJobTitle] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [pendingMigrationBody, setPendingMigrationBody] = useState<MigrationRequestBody | null>(null);
  const [migrationPreview, setMigrationPreview] = useState<MigrationPreview | null>(null);
  const [expandedPreviewPanels, setExpandedPreviewPanels] = useState<Record<PreviewPanelKey, boolean>>({
    blockers: false,
    reasons: false,
    projects: false,
    warnings: false,
  });
  const [toast, setToast] = useState("");

  const config = modeConfig[mode];
  const selectedBrokerageId = getOptionId(selectedBrokerage);
  const selectedUserName = getDisplayName(selectedUser);
  const selectedBrokerageName = getDisplayName(selectedBrokerage);
  const selectedOfficeName = getDisplayName(selectedOffice);
  const selectedOfficeId = getOptionId(selectedOffice);
  const selectedUserId = getOptionId(selectedUser);
  const resolvedJobTitle =
    selectedJobTitle === JOB_TITLE_CUSTOM ? customJobTitle.trim() : selectedJobTitle;

  useEffect(() => {
    let cancelled = false;
    if (!isSignageTeammember || !token) return undefined;

    fetchParentHierarchy(token).then((hierarchy) => {
      if (!cancelled) {
        setParentSignageId(hierarchy?.parentSignageId || null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isSignageTeammember, token]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  };

  const clearMigrationDetails = () => {
    setSelectedUser(null);
    setSelectedOffice(null);
    setSelectedJobTitle("");
    setCustomJobTitle("");
  };

  const handleModeChange = (nextMode: MigrationMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    clearMigrationDetails();
  };

  const handleBrokerageSelect = (brokerage: ApiOption) => {
    setSelectedBrokerage(brokerage);
    clearMigrationDetails();
  };

  const handleUserSelect = (user: ApiOption) => {
    setSelectedUser(user);
    setSelectedJobTitle("");
    setCustomJobTitle("");
    setSelectedOffice(null);
  };

  const resetScreen = () => {
    setMode("agentToTeam");
    setSelectedBrokerage(null);
    clearMigrationDetails();
    setShowConfirm(false);
    setPendingMigrationBody(null);
    setMigrationPreview(null);
  };

  const handleSubmit = async () => {
    if (!token) {
      showToast("Session expired. Please sign in again.");
      return;
    }

    if (!selectedBrokerage) {
      showToast("Please select a brokerage organization.");
      return;
    }

    if (!selectedUser || (config.needJobTitle && (!selectedJobTitle || !selectedOffice))) {
      showToast(config.needJobTitle ? "Please select user, job title and office." : "Please select user.");
      return;
    }

    if (config.needJobTitle && selectedJobTitle === JOB_TITLE_CUSTOM && !customJobTitle.trim()) {
      showToast("Please enter a custom job title.");
      return;
    }

    if (!selectedUserId) {
      showToast("Selected user is missing an id.");
      return;
    }

    if (!selectedBrokerageId) {
      showToast("Selected brokerage is missing an id.");
      return;
    }

    if (config.needJobTitle && !selectedOfficeId) {
      showToast("Please select a valid office.");
      return;
    }

    setPreviewLoading(true);
    setPendingMigrationBody(null);
    setMigrationPreview(null);

    try {
      let jobTitleForPayload = resolvedJobTitle;
      let jobTitleIdForPayload = "";

      if (config.needJobTitle && selectedJobTitle === JOB_TITLE_CUSTOM && customJobTitle.trim()) {
        const createdJobTitle = await createCustomJobTitle(token, customJobTitle.trim());
        jobTitleForPayload = createdJobTitle.title;
        jobTitleIdForPayload = createdJobTitle.id;
      }

      const migrationBody: MigrationRequestBody = {
        target_brokerage_id: selectedBrokerageId,
        office_ids: config.needJobTitle && selectedOfficeId ? [selectedOfficeId] : [],
        ...(config.needJobTitle && jobTitleForPayload ? { job_title: jobTitleForPayload } : {}),
        ...(config.needJobTitle && jobTitleIdForPayload ? { job_title_id: jobTitleIdForPayload } : {}),
      };

      const preview = await postMigrationAction(selectedUserId, token, config.previewPath, "preview", migrationBody);
      setPendingMigrationBody(migrationBody);
      setMigrationPreview(preview);
      setExpandedPreviewPanels(buildExpandedPreviewPanels(preview));
      setShowConfirm(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Migration preview failed.";
      showToast(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmMigrate = async () => {
    if (!token) {
      showToast("Session expired. Please sign in again.");
      return;
    }

    if (!selectedUserId || !pendingMigrationBody) {
      showToast("Migration details are missing. Please try again.");
      return;
    }

    setExecuteLoading(true);

    try {
      await postMigrationAction(selectedUserId, token, config.previewPath, "execute", pendingMigrationBody);
      setShowConfirm(false);
      setPendingMigrationBody(null);
      setMigrationPreview(null);
      resetScreen();
      showToast("User migrated successfully.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Migration failed.";
      showToast(message);
    } finally {
      setExecuteLoading(false);
    }
  };

  return (
    <div className="migration-page">
      <style>{migrationStyles}</style>
      <div className="migration-layout">
        <div className="migration-container">
          <header className="migration-header">
            <div className="migration-header-icon">⇄</div>
            <div>
              <h1>Organization User Migration</h1>
              <p className="migration-header-sub">Migrate an Agent or Teammember to a new license type, job title and office within the same organization.</p>
            </div>
          </header>

          <section className="migration-section">
            <div className="migration-section-title">
              <span className="migration-step-num">⇄</span>
              Migration Type
            </div>
            <div className="migration-seg" role="tablist" aria-label="Migration type">
              <button className={`migration-seg-btn ${mode === "agentToTeam" ? "active" : ""}`} type="button" onClick={() => handleModeChange("agentToTeam")}>
                Agent → Brokerage Teammember
              </button>
              {/* <button className={`migration-seg-btn ${mode === "teamToAgent" ? "active" : ""}`} type="button" onClick={() => handleModeChange("teamToAgent")}>
                Brokerage Teammember → Agent
              </button> */}
            </div>
          </section>

          <section className="migration-section">
            <div className="migration-section-title">
              <span className="migration-step-num">1</span>
              Select Brokerage Organization
            </div>
            <div className="migration-info-banner">
              <span className="migration-info-dot">i</span>
              <div>{config.banner}</div>
            </div>

            <SearchApiDropdown
              label="Search Brokerage Organization"
              placeholder="Search Brokerage Name, Admin Name or Office Name."
              ownerId={signageId}
              token={token}
              licenseType="BROKERAGE_FIRM"
              selectedItem={selectedBrokerage}
              disabled={isSignageTeammember && !signageId}
              emptyText="No brokerages found"
              onSelect={handleBrokerageSelect}
              onClear={() => {
                setSelectedBrokerage(null);
                clearMigrationDetails();
              }}
            />
            {selectedBrokerage && (
              <div className="migration-selected-card show">
                Selected Organization: {selectedBrokerageName} • {formatOfficeCount(selectedBrokerage)} • {formatEmail(selectedBrokerage.email)}
              </div>
            )}
          </section>

          <section className="migration-section">
            <div className="migration-section-title">
              <span className="migration-step-num">2</span>
              Select User &amp; Migration Details
            </div>

            {selectedBrokerage && (
              <div className="migration-org-context">
                <div className="oc-icon">⌂</div>
                <div>
                  <div className="oc-label">Migrating within organization</div>
                  <div className="oc-name">{selectedBrokerageName}</div>
                </div>
              </div>
            )}

            <SearchApiDropdown
              label={config.searchLabel}
              placeholder={selectedBrokerage ? config.searchPlaceholder : "Select a brokerage organization first."}
              ownerId={userId}
              token={token}
              licenseType={config.sourceLicenseType}
              brokerageFirmId={selectedBrokerageId}
              selectedItem={selectedUser}
              disabled={!selectedBrokerage || !userId}
              emptyText={`No ${config.sourceLabel.toLowerCase()}s found in this organization`}
              onSelect={handleUserSelect}
              onClear={clearMigrationDetails}
            />
            <div className="migration-hint">Only users belonging to the selected brokerage are shown. Type badge appears on the right.</div>

            {selectedUser && (
              <div className="migration-selected-card show">
                Selected: {selectedUserName} • {licenseLabel(config.sourceLicenseType)} • {getOfficeName(selectedUser) || "--"} • {formatEmail(selectedUser.email)}
              </div>
            )}

            <div className="migration-transfer-strip">
              <div className="migration-org-box current">
                <div className="migration-org-label">Current Role</div>
                <div className="migration-org-name">{selectedUser ? licenseLabel(config.sourceLicenseType) : "--"}</div>
                <div className="migration-org-meta">{selectedUser ? `Current office • ${getOfficeName(selectedUser) || "--"}` : "Select a user to preview their current role."}</div>
              </div>
              <div className="migration-transfer-arrow">→</div>
              <div className="migration-org-box target">
                <div className="migration-org-label">New Role</div>
                <div className="migration-org-name">{licenseLabel(config.targetLicenseType)}</div>
                <div className="migration-org-meta">
                  New role within same organization{resolvedJobTitle && config.needJobTitle ? ` • ${resolvedJobTitle}` : ""}
                </div>
              </div>
            </div>

            <div className={`migration-options-row ${config.needJobTitle ? "" : "one-col-mode"}`}>
              {config.needJobTitle && (
                <>
                  <SearchJobTitleDropdown
                    token={token}
                    value={selectedJobTitle}
                    onChange={(value) => {
                      setSelectedJobTitle(value);
                      if (value !== JOB_TITLE_CUSTOM) setCustomJobTitle("");
                    }}
                  />
                  {selectedJobTitle === JOB_TITLE_CUSTOM && (
                    <div className="migration-custom-job-field">
                      <label className="migration-label">
                        Custom Job Title <span className="migration-required">*</span>
                      </label>
                      <input
                        type="text"
                        className="migration-custom-job-input"
                        value={customJobTitle}
                        onChange={(event) => setCustomJobTitle(event.target.value)}
                        placeholder="Enter the custom job title"
                        autoComplete="off"
                      />
                    </div>
                  )}
                  <SearchApiDropdown
                    label="Office Name"
                    placeholder={selectedBrokerage ? "Search office name." : "Select a brokerage organization first."}
                    ownerId={userId}
                    token={token}
                    licenseType="OFFICE"
                    brokerageFirmId={selectedBrokerageId}
                    selectedItem={selectedOffice}
                    disabled={!selectedBrokerage || !userId}
                    emptyText="No offices found in this organization"
                    onSelect={setSelectedOffice}
                    onClear={() => setSelectedOffice(null)}
                  />
                </>
              )}
              <div className="migration-derived-license">
                <label className="migration-label">License Type</label>
                <div className="migration-derived-license-box">{licenseLabel(config.targetLicenseType)}</div>
                <div className="migration-hint">Auto selected from migration type.</div>
              </div>
            </div>
          </section>

          <div className="migration-actions">
            <button className="btn back-btn surface-btn fw-bold fs-16" type="button" onClick={resetScreen}>Reset</button>
            <button className="btn btn-primary save-btn surface-btn fw-bold fs-16" type="button" onClick={handleSubmit} disabled={previewLoading}>
              {previewLoading ? "Loading preview..." : "Migrate User"}
            </button>
          </div>
        </div>

        <aside className="migration-summary-card">
          <div className="migration-summary-head">
            <div className="migration-summary-icon">☷</div>
            <div>
              <h2>Migration Summary</h2>
              <p>Preview role, office, and access changes.</p>
            </div>
          </div>
          <div className="migration-summary-body">
            <SummaryRow icon="" label="Organization" value={selectedBrokerageName || "--"} />
            <SummaryRow icon="" label="User" value={selectedUserName || "--"} />
            <SummaryRow icon="" label="Current Type" value={selectedUser ? licenseLabel(config.sourceLicenseType) : "--"} />
            <SummaryRow icon="" label="Target Type" value={licenseLabel(config.targetLicenseType)} />
            {config.needJobTitle && <SummaryRow icon="" label="Job Title" value={resolvedJobTitle || "--"} />}
            {config.needJobTitle && <SummaryRow icon="" label="Office" value={selectedOfficeName || "--"} />}
            <div className="migration-permission-box">
              <div className="migration-permission-title">♙ Permissions Preview</div>
              <ul>
                <li>{selectedUser ? `Selected ${licenseLabel(config.targetLicenseType).toLowerCase()} permission access will apply within ${selectedBrokerageName}.` : "Select a brokerage and user to preview permission changes."}</li>
                <li>
                  {config.needJobTitle
                    ? "The user stays in the same organization; only role, job title and office change."
                    : "The user stays in the same organization; only role changes."}
                </li>
                <li>Previous role permissions will be replaced by the new role.</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>

      {showConfirm && (
        <div className="migration-modal-backdrop show">
          <div className="migration-modal">
            <div className="migration-modal-icon">!</div>
            <h2>Confirm Migration</h2>
            <p>
              Are you sure you want to migrate <b>{selectedUserName}</b> from <b>{licenseLabel(config.sourceLicenseType)}</b> to <b>{licenseLabel(config.targetLicenseType)}</b>
              {config.needJobTitle && <> with job title <b>{resolvedJobTitle}</b> at <b>{selectedOfficeName}</b></>}, within <b>{selectedBrokerageName}</b>?
            </p>
            {hasAnyPreviewAccordionContent(migrationPreview) && (
              <div className="migration-preview-accordion">
                {hasPreviewBlockers(migrationPreview) && (
                  <div className="migration-preview-panel">
                    <button
                      className="migration-preview-panel-head"
                      type="button"
                      onClick={() => togglePreviewPanel("blockers", setExpandedPreviewPanels)}
                      aria-expanded={expandedPreviewPanels.blockers}
                    >
                      <span>Blockers</span>
                      <span>{expandedPreviewPanels.blockers ? "−" : "+"}</span>
                    </button>
                    {expandedPreviewPanels.blockers && (
                      <div className="migration-preview-panel-body">
                        {Object.entries(migrationPreview?.blockers || {}).map(([key, value]) => (
                          <div className="migration-blocker-row" key={key}>
                            <span>{formatBlockerLabel(key)}</span>
                            <b>{value ?? 0}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {hasBlockingReasons(migrationPreview) && (
                  <div className="migration-preview-panel">
                    <button
                      className="migration-preview-panel-head"
                      type="button"
                      onClick={() => togglePreviewPanel("reasons", setExpandedPreviewPanels)}
                      aria-expanded={expandedPreviewPanels.reasons}
                    >
                      <span>Blocking Reasons</span>
                      <span>{expandedPreviewPanels.reasons ? "−" : "+"}</span>
                    </button>
                    {expandedPreviewPanels.reasons && (
                      <div className="migration-preview-panel-body">
                        <ul className="migration-blocking-reasons">
                          {(migrationPreview?.blocking_reasons || []).map((reason) => (
                            <li key={reason}>{reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {hasActiveProjects(migrationPreview) && (
                  <div className="migration-preview-panel">
                    <button
                      className="migration-preview-panel-head"
                      type="button"
                      onClick={() => togglePreviewPanel("projects", setExpandedPreviewPanels)}
                      aria-expanded={expandedPreviewPanels.projects}
                    >
                      <span>Active Projects</span>
                      <span>{expandedPreviewPanels.projects ? "−" : "+"}</span>
                    </button>
                    {expandedPreviewPanels.projects && (
                      <div className="migration-preview-panel-body">
                        {(migrationPreview?.info?.active_projects || []).map((project) => {
                          const projectKey = String(project.id || project.project_number || project.title || "project");
                          return (
                            <div className="migration-project-row" key={projectKey}>
                              <div>
                                <strong>{project.project_number || project.id || "Project"}</strong>
                                {project.title ? ` — ${project.title}` : ""}
                              </div>
                              <span>Status: {formatProjectStatus(project.status)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {hasWarnings(migrationPreview) && (
                  <div className="migration-preview-panel">
                    <button
                      className="migration-preview-panel-head"
                      type="button"
                      onClick={() => togglePreviewPanel("warnings", setExpandedPreviewPanels)}
                      aria-expanded={expandedPreviewPanels.warnings}
                    >
                      <span>Warnings</span>
                      <span>{expandedPreviewPanels.warnings ? "−" : "+"}</span>
                    </button>
                    {expandedPreviewPanels.warnings && (
                      <div className="migration-preview-panel-body">
                        {(migrationPreview?.warnings?.customer_groups?.length ?? 0) > 0 && (
                          <div className="migration-warning-group">
                            <div className="migration-warning-group-title">Customer Groups</div>
                            <ul className="migration-preview-list">
                              {(migrationPreview?.warnings?.customer_groups || []).map((item) => (
                                <li key={item.id || item.name}>{item.name || "--"}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(migrationPreview?.warnings?.payment_methods?.length ?? 0) > 0 && (
                          <div className="migration-warning-group">
                            <div className="migration-warning-group-title">Payment Methods</div>
                            <ul className="migration-preview-list">
                              {(migrationPreview?.warnings?.payment_methods || []).map((item) => (
                                <li key={item.id || item.name}>
                                  {item.name || "--"}
                                  {(item.groups?.length ?? 0) > 0 && (
                                    <ul className="migration-preview-sublist">
                                      {(item.groups || []).map((group) => (
                                        <li key={group.id || group.name}>{group.name || "--"}</li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(migrationPreview?.warnings?.discounts?.length ?? 0) > 0 && (
                          <div className="migration-warning-group">
                            <div className="migration-warning-group-title">Discounts</div>
                            <ul className="migration-preview-list">
                              {(migrationPreview?.warnings?.discounts || []).map((item) => (
                                <li key={item.id || item.name}>{item.name || "--"}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(migrationPreview?.warnings?.special_fees?.length ?? 0) > 0 && (
                          <div className="migration-warning-group">
                            <div className="migration-warning-group-title">Special Fees</div>
                            <ul className="migration-preview-list">
                              {(migrationPreview?.warnings?.special_fees || []).map((item) => (
                                <li key={item.id || item.name}>{item.name || "--"}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="migration-warning-footer-note">{MIGRATION_WARNINGS_REMOVAL_NOTE}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="migration-modal-actions">
              <button className="btn back-btn surface-btn fw-bold fs-16" type="button" onClick={() => { setShowConfirm(false); setPendingMigrationBody(null); setMigrationPreview(null); }} disabled={executeLoading}>
                No, Cancel
              </button>
              <button className="btn btn-primary save-btn surface-btn fw-bold fs-16" type="button" onClick={handleConfirmMigrate} disabled={executeLoading}>
                {executeLoading ? "Migrating..." : "Yes, Migrate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`migration-toast ${toast ? "show" : ""}`}>{toast || "User migrated successfully."}</div>
    </div>
  );
}

function SearchApiDropdown({
  label,
  placeholder,
  ownerId,
  token,
  licenseType,
  brokerageFirmId,
  selectedItem,
  disabled,
  emptyText,
  onSelect,
  onClear,
}: {
  label: string;
  placeholder: string;
  ownerId: string;
  token: string;
  licenseType: LicenseType;
  brokerageFirmId?: string;
  selectedItem: ApiOption | null;
  disabled?: boolean;
  emptyText: string;
  onSelect: (item: ApiOption) => void;
  onClear: () => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ApiOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const lastRequestKeyRef = useRef("");

  const canFetch = Boolean(ownerId && token && API_BASE && !disabled && (!brokerageFirmId || licenseType !== "BROKERAGE_FIRM"));

  const loadOptions = useCallback(
    async (nextQuery: string, nextPage: number, append = false) => {
      if (!canFetch) {
        setItems([]);
        return;
      }

      const requestKey = `${ownerId}|${licenseType}|${brokerageFirmId || ""}|${nextQuery.trim()}|${nextPage}|${append ? "append" : "replace"}`;
      if (!append && requestKey === lastRequestKeyRef.current) {
        return;
      }
      lastRequestKeyRef.current = requestKey;

      const requestId = ++requestRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const json = await fetchCreatedOnlyUsers({
          ownerId,
          token,
          query: nextQuery,
          page: nextPage,
          licenseType,
          brokerageFirmId,
          signal: controller.signal,
        });

        if (requestId !== requestRef.current) return;
        const data = Array.isArray(json?.data) ? json.data : [];
        setItems((prev) => (append ? [...prev, ...data] : data));

        const currentPage = Number(json?.page ?? nextPage);
        const totalPages = Number(json?.totalPages ?? currentPage);
        setPage(currentPage);
        setHasMore(currentPage < totalPages && data.length >= DEFAULT_LIMIT);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Migration search error:", error);
          if (requestId === requestRef.current) setItems([]);
        }
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [brokerageFirmId, canFetch, licenseType, ownerId, token]
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(false);
    lastRequestKeyRef.current = "";
  }, [brokerageFirmId, licenseType, ownerId]);

  useEffect(() => {
    if (!open || !canFetch) return undefined;
    const timer = window.setTimeout(() => {
      setPage(1);
      loadOptions(query, 1, false);
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [canFetch, loadOptions, open, query]);

  useEffect(() => {
    setInputValue(selectedItem ? getDisplayName(selectedItem) : "");
  }, [selectedItem]);

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleClear = () => {
    setInputValue("");
    setQuery("");
    setOpen(false);
    onClear();
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      loadOptions(query, page + 1, true);
    }
  };

  return (
    <div className={`search-bat-wrap migration-search ${disabled ? "search-bat--readonly" : ""}`}>
      <label className="migration-label">
        {label} <span className="migration-required">*</span>
      </label>
      <div className="search-bat-search-wrap">
        <form className="search-bat-search-box" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <span className="search-bat-search-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="search"
            className="search-bat-search-input"
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={disabled}
            onFocus={handleFocus}
            onChange={(event) => {
              const value = event.target.value;
              setInputValue(value);
              setQuery(value);
              setOpen(true);
            }}
            autoComplete="off"
          />
          <button className="search-bat-clear-btn" type="button" onClick={handleClear} disabled={disabled} aria-label="Clear">
            ×
          </button>
        </form>

        {!disabled && (
          <div className={`search-bat-dropdown ${open ? "" : "search-bat-hidden"}`} ref={listRef} onScroll={handleScroll}>
            {items.length === 0 ? (
              <div className="migration-empty">{loading ? "Loading..." : emptyText}</div>
            ) : (
              items.map((item, index) => (
                <SearchRow
                  key={`${getOptionId(item) || index}-${licenseType}`}
                  item={item}
                  fallbackLicenseType={licenseType}
                  onClick={() => {
                    const normalizedItem = normalizeApiOption(item, licenseType);
                    onSelect(normalizedItem);
                    setInputValue(getDisplayName(normalizedItem));
                    setOpen(false);
                  }}
                />
              ))
            )}
            {loading && items.length > 0 && <div className="migration-empty">Loading...</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchRow({ item, fallbackLicenseType, onClick }: { item: ApiOption; fallbackLicenseType: LicenseType; onClick: () => void }) {
  const normalizedItem = normalizeApiOption(item, fallbackLicenseType);
  const effectiveLicenseType = normalizedItem.license_type || fallbackLicenseType;
  const badge = badgeForLicense(effectiveLicenseType);
  const bg =
    effectiveLicenseType === "BROKERAGE_FIRM"
      ? "#EEF3FF"
      : effectiveLicenseType === "OFFICE"
        ? "#F3E8FF"
        : effectiveLicenseType === "BROKERAGE_TEAM_MEMBER"
          ? "#FFF4E6"
          : "#EDFAF4";
  const image = normalizedItem.profile_pic_url || normalizedItem.logo_url || normalizedItem.image || DEFAULT_IMAGE;

  return (
    <div className="search-bat-drop-row" role="button" tabIndex={0} onClick={onClick} onKeyDown={(event) => event.key === "Enter" && onClick()}>
      <div className="search-bat-drop-icon" style={{ background: bg }}>
        <img
          className="search-bat-drop-img"
          src={image}
          alt=""
          onError={(event) => {
            const img = event.currentTarget;
            if (img.src !== DEFAULT_IMAGE) {
              img.onerror = null;
              img.src = DEFAULT_IMAGE;
            }
          }}
        />
      </div>
      <div className="search-bat-drop-main">
        <div className="search-bat-drop-text">
          <div className="search-bat-drop-name">{getDisplayName(normalizedItem) || "--"}</div>
          <div className="search-bat-drop-email">{formatEmail(normalizedItem.email)}</div>
          <div className="search-bat-drop-meta">{optionMeta(normalizedItem)}</div>
        </div>
        <span className={`search-bat-badge ${badge.className}`}>{badge.label}</span>
      </div>
    </div>
  );
}

function SearchJobTitleDropdown({ token, value, onChange }: { token: string; value: string; onChange: (value: string) => void }) {
  const [inputValue, setInputValue] = useState(value);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<JobTitleOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestRef = useRef(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastRequestKeyRef = useRef("");

  const loadOptions = useCallback(
    async (nextQuery: string, nextPage: number, append = false) => {
      if (!token || !API_BASE) {
        setItems([{ value: JOB_TITLE_CUSTOM, label: "Custom", id: JOB_TITLE_CUSTOM }]);
        return;
      }

      const requestKey = `${nextQuery.trim()}|${nextPage}|${append ? "append" : "replace"}`;
      if (!append && requestKey === lastRequestKeyRef.current) {
        return;
      }
      lastRequestKeyRef.current = requestKey;

      const requestId = ++requestRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(nextPage),
          limit: String(DEFAULT_LIMIT),
        });

        if (nextQuery.trim()) {
          params.set("search", nextQuery.trim());
        }

        const response = await fetch(`${API_BASE}/job-titles/select2?${params}`, {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || result?.error || "Failed to load job titles");
        }

        if (requestId !== requestRef.current) return;
        const apiOptions: JobTitleOption[] = Array.isArray(result?.results)
          ? result.results
              .filter((item: any) => item?.status !== false)
              .map((item: any) => ({
                value: String(item?.title || item?.text || item?.id || item?._id || ""),
                label: String(item?.text || item?.title || ""),
                id: String(item?.id || item?._id || ""),
              }))
              .filter((item: JobTitleOption) => item.value && item.label)
          : [];
        const nextItems = [...apiOptions, { value: JOB_TITLE_CUSTOM, label: "Custom", id: JOB_TITLE_CUSTOM }];

        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setPage(nextPage);
        setHasMore(nextPage * DEFAULT_LIMIT < Number(result?.total || 0));
      } catch (error: any) {
        if (error?.name === "AbortError") return;
        console.warn("Failed to load job title options:", error);
        if (requestId === requestRef.current) {
          setItems([{ value: JOB_TITLE_CUSTOM, label: "Custom", id: JOB_TITLE_CUSTOM }]);
          setHasMore(false);
        }
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      setPage(1);
      loadOptions(query, 1, false);
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [loadOptions, open, query]);

  const handleFocus = () => {
    setOpen(true);
  };

  const handleClear = () => {
    setInputValue("");
    setQuery("");
    setOpen(false);
    onChange("");
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      loadOptions(query, page + 1, true);
    }
  };

  return (
    <div className="search-bat-wrap migration-search">
      <label className="migration-label">
        Job Title <span className="migration-required">*</span>
      </label>
      <div className="search-bat-search-wrap">
        <form className="search-bat-search-box" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <span className="search-bat-search-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="search"
            className="search-bat-search-input"
            value={inputValue}
            placeholder="Select job title / role"
            onFocus={handleFocus}
            onChange={(event) => {
              const nextValue = event.target.value;
              setInputValue(nextValue);
              setQuery(nextValue);
              setOpen(true);
            }}
            autoComplete="off"
          />
          <button className="search-bat-clear-btn" type="button" onClick={handleClear} aria-label="Clear">
            ×
          </button>
        </form>
        <div className={`search-bat-dropdown ${open ? "" : "search-bat-hidden"}`} ref={listRef} onScroll={handleScroll}>
          {items.length === 0 ? (
            <div className="migration-empty">{loading ? "Loading..." : "No job titles found"}</div>
          ) : (
            items.map((item) => (
              <button
                className="migration-job-option"
                type="button"
                key={`${item.id}-${item.value}`}
                onClick={() => {
                  onChange(item.value);
                  setInputValue(item.label);
                  setOpen(false);
                }}
              >
                {item.label}
              </button>
            ))
          )}
          {loading && items.length > 0 && <div className="migration-empty">Loading...</div>}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="migration-summary-row">
      <div className="migration-summary-key">
        <span>{icon}</span>
        {label}
      </div>
      <div className="migration-summary-value">{value}</div>
    </div>
  );
}

const migrationStyles = `
.migration-page{min-height:100vh;padding:28px 18px 42px;font-family:'Roboto',sans-serif;font-size:14px;color:#0f172a;background:linear-gradient(180deg,#fbfdff 0%,#f4f7fb 100%)}
.migration-page *,.migration-page button,.migration-page input,.migration-page select,.migration-page textarea,.migration-page label,.migration-page h1,.migration-page h2,.migration-page p,.migration-page li,.migration-page ul{font-family:'Roboto',sans-serif}
.migration-page .search-bat-wrap,.migration-page .search-bat-wrap *{font-family:'Roboto',sans-serif}
.migration-layout{max-width:1380px;margin:0 auto;display:grid;grid-template-columns:minmax(0,1fr) 390px;gap:24px;align-items:start}
.migration-container,.migration-summary-card{background:rgba(255,255,255,.96);border:1px solid #e4e9f2;border-radius:24px;box-shadow:0 20px 55px rgba(15,23,42,.12);overflow:hidden}
.migration-container{overflow:visible;position:relative;z-index:1;border-radius:24px}
.migration-header{display:flex;align-items:center;gap:18px;padding:30px 34px;border-bottom:1px solid #e5eaf2;background:linear-gradient(180deg,#fff 0%,#fbfdff 100%);border-radius:24px 24px 0 0;overflow:hidden}
.migration-actions{border-radius:0 0 24px 24px;overflow:hidden}
.migration-header-icon{width:50px;height:50px;border-radius:17px;display:grid;place-items:center;background:#dcfce7;color:#059669;font-size:28px;font-weight:700}
.migration-header h1{margin:0;font-size:22px;letter-spacing:.02em}
.migration-header-sub{margin:6px 0 0;color:#404954;font-weight:400;font-size:14px;line-height:1.5}
.migration-section{padding:30px 34px;border-bottom:1px solid #e5eaf2;position:relative;overflow:visible}
.migration-section-title{display:flex;align-items:center;gap:13px;margin-bottom:24px;font-size:18px;font-weight:500;letter-spacing:.01em}
.migration-step-num{width:36px;height:36px;border-radius:12px;display:grid;place-items:center;background:#eef4ff;color:#2563eb;font-size:14px;font-weight:700}
.migration-label{display:block;margin-bottom:10px;font-size:14px;font-weight:700;letter-spacing:.025em;color:#0f172a}
.migration-required{color:#ef4444;margin-left:4px}
.migration-hint{font-size:14px;color:#64748b;margin-top:9px;font-weight:500;line-height:1.5}
.migration-seg{display:inline-flex;background:#eef2f7;border:1px solid #e2e8f0;border-radius:14px;padding:5px;gap:4px}
.migration-seg-btn{border:0;background:transparent;font-family:'Roboto',sans-serif;font-size:14px;font-weight:600;color:#64748b;padding:11px 18px;border-radius:10px;cursor:pointer;transition:.15s;white-space:nowrap}
.migration-seg-btn.active{background:#fff;color:#1d4ed8;box-shadow:0 2px 8px rgba(15,23,42,.10)}
.migration-info-banner{display:flex;gap:12px;align-items:flex-start;margin-bottom:22px;padding:16px 18px;border:1px solid #dbe8ff;border-radius:17px;background:linear-gradient(135deg,#f7fbff,#eef5ff);color:#334155;font-size:14px;font-weight:500;line-height:1.5}
.migration-info-dot{width:28px;height:28px;display:grid;place-items:center;flex:0 0 28px;border-radius:10px;background:#dbeafe;color:#2563eb;font-weight:700}
.migration-search{position:relative}
.migration-search:focus-within{z-index:50}
.migration-search .search-bat-search-wrap{margin-top:0;margin-bottom:14px;position:relative}
.migration-search .search-bat-search-box{height:60px;border-radius:15px;border:1.6px solid #cfd8e6}
.migration-search .search-bat-search-box:focus-within{border-color:#4474f7;box-shadow:0 0 0 4px rgba(37,99,235,.10),0 10px 22px rgba(15,23,42,.06)}
.migration-search .search-bat-search-input{font-family:'Roboto',sans-serif;font-size:14px;font-weight:600;color:#111827}
.migration-search .search-bat-drop-row,.migration-search .search-bat-drop-name,.migration-search .search-bat-drop-meta{font-family:'Roboto',sans-serif;font-size:14px}
.migration-search .search-bat-dropdown{border-radius:17px;max-height:280px;box-shadow:0 20px 50px rgba(15,23,42,.16);z-index:1100}
.migration-search .search-bat-drop-row{min-height:86px;padding-top:14px;padding-bottom:14px}
.migration-selected-card{display:none;margin-top:14px;padding:15px 16px;border:1px solid #dce7f5;background:linear-gradient(180deg,#f8fbff,#fff);border-radius:15px;font-size:14px;font-weight:600;color:#334155;box-shadow:0 6px 18px rgba(15,23,42,.04)}
.migration-selected-card.show{display:flex;align-items:center;gap:10px}
.migration-selected-card.show:before{content:'✓';display:grid;place-items:center;width:22px;height:22px;border-radius:50%;background:#dcfce7;color:#047857;font-size:14px;font-weight:700;flex:0 0 22px}
.migration-org-context{display:flex;align-items:center;gap:14px;margin-bottom:22px;padding:16px 18px;border:1px solid #cdebda;border-radius:17px;background:linear-gradient(135deg,#f4fbf7,#ecf8f1)}
.migration-org-context .oc-icon{width:42px;height:42px;flex:0 0 42px;border-radius:13px;display:grid;place-items:center;background:#dcfce7;color:#047857;font-size:20px;font-weight:700}
.migration-org-context .oc-label{font-size:12px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#15803d}
.migration-org-context .oc-name{font-size:16px;font-weight:500;color:#0f172a;margin-top:2px}
.migration-transfer-strip{display:grid;grid-template-columns:1fr 52px 1fr;gap:14px;align-items:stretch;margin:22px 0}
.migration-org-box{border:1px solid #e2e8f0;border-radius:18px;padding:18px;background:#fff;box-shadow:0 6px 18px rgba(15,23,42,.04)}
.migration-org-box.current{background:linear-gradient(180deg,#fbfdff,#fff)}
.migration-org-box.target{background:linear-gradient(180deg,#f6fbf8,#fff);border-color:#cdebda}
.migration-org-label{font-size:12px;font-weight:700;letter-spacing:.08em;color:#64748b;text-transform:uppercase;margin-bottom:12px}
.migration-org-name{font-size:16px;font-weight:500;color:#0f172a;margin-bottom:7px}
.migration-org-meta{font-size:14px;font-weight:600;color:#64748b;line-height:1.45}
.migration-transfer-arrow{height:52px;width:52px;border-radius:17px;background:#eef4ff;color:#2563eb;display:grid;place-items:center;font-size:24px;font-weight:700;align-self:center}
.migration-options-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px;align-items:start}
.migration-options-row.one-col-mode{grid-template-columns:minmax(0,1fr)}
.migration-native-select,.migration-derived-license-box{height:58px;border:1.5px solid #cfd8e6;border-radius:15px;background:#f8fafc;padding:0 16px;font-family:'Roboto',sans-serif;font-size:14px;font-weight:700;color:#111827}
.migration-derived-license-box{display:flex;align-items:center;background:#eef4ff;color:#1d4ed8}
.migration-custom-job-input{width:100%;height:60px;border:1.6px solid #cfd8e6;border-radius:15px;background:#fff;padding:0 16px;font-family:'Roboto',sans-serif;font-size:14px;font-weight:600;color:#111827;outline:none}
.migration-custom-job-input:focus{border-color:#4474f7;box-shadow:0 0 0 4px rgba(37,99,235,.10),0 10px 22px rgba(15,23,42,.06)}
.migration-job-option{width:100%;border:0;background:#fff;text-align:left;padding:15px 17px;font-family:'Roboto',sans-serif;font-size:14px;font-weight:700;cursor:pointer;border-bottom:1px solid #eef2f7;color:#0f172a}
.migration-job-option:hover{background:#eef4ff;color:#1d4ed8}
.migration-summary-card{position:sticky;top:28px}
.migration-summary-head{display:flex;gap:16px;padding:28px 32px;border-bottom:1px solid #e5eaf2;background:linear-gradient(180deg,#fff,#fbfdff);border-radius:24px 24px 0 0;overflow:hidden}
.migration-summary-icon{width:48px;height:48px;border-radius:16px;background:#eef4ff;color:#2563eb;display:grid;place-items:center;font-size:22px;font-weight:700;flex:0 0 48px}
.migration-summary-head h2{margin:0;font-size:18px;line-height:1.15;font-weight:500}
.migration-summary-head p{margin:9px 0 0;color:#64748b;font-size:14px;font-weight:600;line-height:1.45}
.migration-summary-body{padding:28px 32px 30px}
.migration-summary-row{display:grid;grid-template-columns:130px 1fr;gap:12px;padding:17px 0;border-bottom:1px dashed #dfe5ee}
.migration-summary-key{display:flex;align-items:center;gap:10px;color:#64748b;font-weight:600;font-size:14px}
.migration-summary-value{text-align:right;font-size:14px;font-weight:500;color:#111827;line-height:1.35;word-break:break-word}
.migration-permission-box{margin-top:22px;border:1px solid #bbf7d0;background:#dcfce7;border-radius:18px;padding:20px 22px;color:#14532d}
.migration-permission-title{margin:0 0 12px;font-size:14px;font-weight:700}
.migration-permission-box ul{margin:0;padding-left:22px}
.migration-permission-box li{margin:9px 0;font-size:14px;font-weight:600;line-height:1.45}
.migration-actions{display:flex;justify-content:flex-end;gap:14px;padding:24px 34px 32px;background:#fff}
.migration-btn{border:0;border-radius:13px;padding:15px 26px;font-family:'Roboto',sans-serif;font-weight:700;font-size:14px;cursor:pointer;transition:.15s}
.migration-btn-primary{background:linear-gradient(180deg,#316cf6,#2359d9);color:#fff;box-shadow:0 12px 22px rgba(37,99,235,.25)}
.migration-btn-secondary{background:#eef2f7;color:#0f172a}
.migration-modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.50);display:none;align-items:center;justify-content:center;z-index:120;padding:20px;backdrop-filter:blur(3px)}
.migration-modal-backdrop.show{display:flex}
.migration-modal{width:min(680px,100%);max-height:calc(100vh - 44px);overflow:auto;background:#fff;border-radius:24px;padding:30px;text-align:center;box-shadow:0 20px 55px rgba(15,23,42,.12);margin-top:56px;font-family:'Roboto',sans-serif;font-size:14px}
.migration-modal-icon{width:60px;height:60px;border-radius:20px;background:#fff7ed;color:#f97316;display:grid;place-items:center;margin:0 auto 16px;font-size:28px;font-weight:700}
.migration-modal h2{margin:0 0 10px;font-size:21px;font-weight:500}
.migration-modal p{margin:0 0 22px;color:#475569;font-size:15px;line-height:1.6;font-weight:500}
.migration-preview-accordion{display:grid;gap:12px;margin:0 0 24px;text-align:left}
.migration-preview-panel{border:1px solid #e2e8f0;border-radius:16px;background:#f8fafc;overflow:hidden}
.migration-preview-panel-head{width:100%;border:0;background:#fff;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:15px 18px;color:#0f172a;font-family:'Roboto',sans-serif;font-size:16px;font-weight:700;cursor:pointer}
.migration-preview-panel-head[aria-expanded="true"]{background:#eef4ff;color:#1d4ed8}
.migration-preview-panel-body{padding:14px 18px 16px;border-top:1px solid #e2e8f0}
.migration-blocker-row{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:10px 0;color:#475569;font-size:15px;font-weight:600;border-bottom:1px dashed #dbe3ee}
.migration-blocker-row:last-child{border-bottom:0}
.migration-blocker-row b{min-width:42px;text-align:center;border-radius:999px;background:#fff7ed;color:#ea580c;padding:5px 10px;font-size:15px}
.migration-blocking-reasons{margin:0;padding-left:20px;color:#475569}
.migration-blocking-reasons li{margin:9px 0;font-size:15px;font-weight:600;line-height:1.45}
.migration-preview-list{margin:0;padding-left:20px;color:#475569}
.migration-preview-list li{margin:8px 0;font-size:15px;font-weight:500;line-height:1.45}
.migration-preview-sublist{margin:6px 0 0;padding-left:18px;list-style:circle}
.migration-preview-sublist li{margin:4px 0;font-size:14px;font-weight:600;color:#64748b}
.migration-warning-group{margin-bottom:16px}
.migration-warning-group:last-child{margin-bottom:0}
.migration-warning-group-title{font-size:14px;font-weight:600;color:#0f172a;margin-bottom:8px;text-transform:uppercase;letter-spacing:.04em}
.migration-warning-footer-note{margin:14px 0 0;padding:12px 14px;border-radius:12px;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412;font-size:14px;font-weight:600;line-height:1.5}
.migration-project-row{padding:10px 0;color:#475569;font-size:15px;font-weight:600;line-height:1.45;border-bottom:1px dashed #dbe3ee}
.migration-project-row:last-child{border-bottom:0}
.migration-project-row span{display:block;margin-top:4px;color:#64748b;font-size:14px;font-weight:600}
.migration-modal-actions{display:flex;justify-content:center;gap:12px}
.migration-toast{position:fixed;right:22px;bottom:22px;background:#101828;color:#fff;padding:15px 18px;border-radius:14px;box-shadow:0 20px 55px rgba(15,23,42,.12);display:none;z-index:130;font-family:'Roboto',sans-serif;font-size:14px;font-weight:600}
.migration-toast.show{display:block}
.migration-empty{padding:20px;text-align:center;color:#7b8baa;font-family:'Roboto',sans-serif;font-size:14px}
@media(max-width:1120px){.migration-layout{grid-template-columns:1fr}.migration-summary-card{position:static}}
@media(max-width:760px){
  .migration-page{padding:14px}
  .migration-header,.migration-section,.migration-actions{padding-left:18px;padding-right:18px}
  .migration-options-row,.migration-options-row.one-col-mode,.migration-transfer-strip{grid-template-columns:1fr}
  .migration-transfer-arrow{transform:rotate(90deg);justify-self:center}
  .migration-summary-row{grid-template-columns:1fr}
  .migration-summary-value{text-align:left}
  .migration-actions,.migration-modal-actions{flex-direction:column-reverse}
  .migration-btn{width:100%}
  .migration-seg{display:flex;width:100%}
  .migration-seg-btn{flex:1;justify-content:center;padding:11px 8px;font-size:14px}
}
`;
